// src/pages/User/NewRequest/usecases/products/addProductToCart.ts
import { LastPayment } from "../../../model/form.model";

import { CartItem } from "../../../model/product.cart.model";
import type { NewRequestServices } from "../../../model/services.model";
import { CustomerProduct, FreightType } from "../../../model/customer.model";

import { validateCartItem } from "../../../domain/validators/validateCartItem";
import { createPaymentDayOptions } from "../../../domain/policies/paymentPolicy";

export async function addProductToCart(args: {
  services: NewRequestServices;
  freightType: FreightType;

  customer: {
    id: string;
  };
  filialId: string;
  address: {
    id: string;
    transportationZone: string;
  };

  currentCart: CartItem[];

  input: {
    code: string;
    description: string;
    quantity: number;
  };

  products: CustomerProduct[];
  lastPayment: LastPayment | null;
  isCashPaymentEnabled: boolean;
}): Promise<CartItem[]> {
  const validation = validateCartItem({
    productId: args.input.code,
    description: args.input.description,
    quantity: args.input.quantity,
  });
  if (!validation.ok) throw new Error(validation.reason);

  const quantity = Math.max(0, Math.floor(Number(args.input.quantity)));

  const lastDeadline = args.lastPayment?.[args.input.code]?.deadlinePayment;

  const catalogPayCond = Number(
    args.products?.find((p) => String(p.id) === String(args.input.code))
      ?.payCond
  );

  const deadlinePayment =
    typeof lastDeadline === "number"
      ? lastDeadline
      : Number.isFinite(catalogPayCond)
      ? catalogPayCond
      : 0;

  const payment: "T" | "B" =
    deadlinePayment > 0 || !args.isCashPaymentEnabled ? "B" : "T";

  const priced = await args.services.productService.execute({
    addressID: args.address.id,
    deadlinePayment,
    productID: args.input.code,
    customerID: args.customer.id,
    filialID: args.filialId,
    transportationZone: args.address.transportationZone,
    intendedVolume: quantity || undefined,
    incoterms: args.freightType || undefined,
  });

  const subtotal = Number(priced.subtotal) || 0;
  const total = subtotal;

  const item: CartItem = {
    id: uuidLike(),

    productId: args.input.code,
    code: args.input.code,
    description: args.input.description,

    customerId: args.customer.id,
    filialId: args.filialId,
    addressId: args.address.id,
    transportationZone: args.address.transportationZone,

    payment,
    payCondMax: Number(priced.payCond),
    deadlinePayment,
    paymentDayOptions: createPaymentDayOptions(
      Number(args?.products?.find((p) => p?.id === priced?.productID)?.payCond)
    ),

    unitPrice: priced.price,
    freightUnitPrice: priced.freight,

    quantity,

    subtotal,
    total,
  };

  return [...args.currentCart, item];
}

function uuidLike(): string {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
